import { useCallback, useEffect, useMemo, useState } from "react";import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, KeyRound } from "lucide-react";
import DataTable from "@/components/DataTable";

const ROLES = ["admin", "doctor", "receptionist"];

const ROLE_TINT = {
  admin: "bg-[color:var(--primary-light)]/60 text-[color:var(--primary-hover)]",
  doctor: "bg-[color:var(--secondary-light)]/60 text-[color:var(--secondary)]",
  receptionist: "bg-[color:var(--lavender-light)]/60 text-[color:var(--lavender)]",
  patient: "bg-[color:var(--bg-marketing)] text-[color:var(--muted)]",
};

export default function Staff() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [editing, setEditing] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/users");
      setUsers(data);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load users");
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const bulkDelete = async (ids) => {
    if (ids.includes(user?.id)) {
      toast.error("You can't delete your own admin account");
      return;
    }
    try {
      const { data } = await api.post("/users/bulk-delete", { ids });
      toast.success(`Deleted ${data.deleted} user(s)`);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Bulk delete failed"); }
  };

  const renderRowExtras = useCallback((u) => <ResetPasswordButton user={u} />, []);

  const columns = useMemo(() => ([
    {
      key: "name", label: "Name", sortable: true, accessor: (u) => u.name,
      render: (u) => (
        <div>
          <p className="font-medium text-[color:var(--ink)]">{u.name}{u.id === user?.id && <span className="ml-2 text-[10px] text-[color:var(--muted)] uppercase tracking-wider">(you)</span>}</p>
          <p className="text-xs text-[color:var(--muted)] sm:hidden">{u.email}</p>
        </div>
      ),
    },
    { key: "email", label: "Email", sortable: true, accessor: (u) => u.email, render: (u) => <span className="text-[color:var(--muted)]">{u.email}</span> },
    { key: "phone", label: "Phone", sortable: true, defaultHidden: true, accessor: (u) => u.phone || "" },
    {
      key: "role", label: "Role", sortable: true, accessor: (u) => u.role,
      render: (u) => <span className={`text-[10px] tracking-wider uppercase px-2.5 py-1 rounded-full ${ROLE_TINT[u.role] || ""}`}>{u.role}</span>,
    },
    {
      key: "created_at", label: "Created", sortable: true, accessor: (u) => u.created_at,
      render: (u) => <span className="text-[color:var(--muted)]">{u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}</span>,
    },
  ]), [user?.id]);

  return (
    <div data-testid="staff-page">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-7">
        <div>
          <p className="text-xs font-semibold tracking-[0.2em] uppercase text-[color:var(--muted)]">Access control</p>
          <h1 className="font-heading text-3xl md:text-4xl text-[color:var(--ink)] font-medium mt-1">Staff & users</h1>
        </div>
        <StaffDialog open={createOpen} setOpen={setCreateOpen} onSaved={load} />
      </div>

      <DataTable
        storageKey="users"
        rows={users}
        columns={columns}
        isAdmin
        onEdit={setEditing}
        onDeleteMany={bulkDelete}
        emptyMessage="No users yet."
        extraRowActions={renderRowExtras}
      />

      {editing && (
        <StaffDialog
          open={!!editing}
          setOpen={(v) => { if (!v) setEditing(null); }}
          existing={editing}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}

      <p className="text-xs text-[color:var(--muted)] mt-4">
        Role tints: <span className={`px-2 py-0.5 rounded-full ${ROLE_TINT.admin}`}>admin</span>{" "}
        <span className={`px-2 py-0.5 rounded-full ${ROLE_TINT.doctor}`}>doctor</span>{" "}
        <span className={`px-2 py-0.5 rounded-full ${ROLE_TINT.receptionist}`}>receptionist</span>{" "}
        <span className={`px-2 py-0.5 rounded-full ${ROLE_TINT.patient}`}>patient</span>
      </p>
    </div>
  );
}

function StaffDialog({ open, setOpen, onSaved, existing }) {
  const isEdit = !!existing;
  const empty = { name: "", email: "", phone: "", password: "", role: "doctor" };
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (existing) setForm({ name: existing.name || "", email: existing.email || "", phone: existing.phone || "", password: "", role: existing.role || "doctor" });
    else setForm(empty);
  }, [existing, open]);

  const save = async () => {
    if (!form.name || !form.email) { toast.error("Name and email are required"); return; }
    if (!isEdit && form.password.length < 6) { toast.error("Initial password must be 6+ characters"); return; }
    setLoading(true);
    try {
      if (isEdit) {
        await api.put(`/users/${existing.id}`, { name: form.name, phone: form.phone, role: form.role });
        toast.success("User updated");
      } else {
        await api.post("/users", form);
        toast.success("Staff member created");
      }
      setOpen(false);
      onSaved();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Save failed"); }
    finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {!isEdit && (
        <DialogTrigger asChild>
          <Button className="rounded-full bg-[color:var(--primary)] hover:bg-[color:var(--primary-hover)] text-white" data-testid="create-staff-btn">
            <Plus className="w-4 h-4 mr-1.5" /> Add staff
          </Button>
        </DialogTrigger>
      )}
      <DialogContent className="max-w-lg" data-testid="staff-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">{isEdit ? "Edit user" : "Add a staff member"}</DialogTitle>
          <DialogDescription className="text-[color:var(--muted)]">
            {isEdit ? "Update name, phone or role. Use the key icon on the row to reset the password." : "Set an initial password — they can change it after first login."}
          </DialogDescription>
        </DialogHeader>
        <div className="grid md:grid-cols-2 gap-4 mt-2">
          <Field k="name" l="Full name *" form={form} setForm={setForm} />
          <Field k="email" l="Email *" form={form} setForm={setForm} type="email" disabled={isEdit} />
          <Field k="phone" l="Phone" form={form} setForm={setForm} />
          {!isEdit && <Field k="password" l="Initial password *" form={form} setForm={setForm} />}
          <div className="md:col-span-2">
            <label className="text-xs font-semibold tracking-wider uppercase text-[color:var(--muted)]">Role *</label>
            <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
              <SelectTrigger className="mt-1.5" data-testid="staff-role"><SelectValue /></SelectTrigger>
              <SelectContent>
                {(isEdit ? [...ROLES, "patient"] : ROLES).map((r) => <SelectItem key={r} value={r} className="capitalize">{r}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} data-testid="staff-cancel">Cancel</Button>
          <Button onClick={save} disabled={loading} className="bg-[color:var(--primary)] hover:bg-[color:var(--primary-hover)] text-white" data-testid="staff-save">
            {loading ? "Saving…" : isEdit ? "Save changes" : "Create"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ k, l, form, setForm, type = "text", disabled = false }) {
  return (
    <div>
      <label className="text-xs font-semibold tracking-wider uppercase text-[color:var(--muted)]">{l}</label>
      <Input type={type} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="mt-1.5" disabled={disabled} data-testid={`staff-${k}`} />
    </div>
  );
}

function ResetPasswordButton({ user }) {
  const [open, setOpen] = useState(false);
  const [pwd, setPwd] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    if (pwd.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    setLoading(true);
    try {
      await api.post(`/users/${user.id}/reset-password`, { new_password: pwd });
      toast.success(`Password reset for ${user.name}`);
      setOpen(false);
      setPwd("");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Reset failed");
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setPwd(""); }}>
      <DialogTrigger asChild>
        <button
          className="text-[color:var(--muted)] hover:text-[color:var(--primary)] p-1"
          aria-label="Reset password"
          data-testid={`user-reset-${user.id}`}
          title="Reset password"
        >
          <KeyRound className="w-4 h-4" />
        </button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="font-heading">Reset password</DialogTitle>
          <DialogDescription className="text-[color:var(--muted)]">For <strong className="text-[color:var(--ink)]">{user.name}</strong> ({user.email}).</DialogDescription>
        </DialogHeader>
        <div>
          <label className="text-xs font-semibold tracking-wider uppercase text-[color:var(--muted)]">New password</label>
          <Input type="text" value={pwd} onChange={(e) => setPwd(e.target.value)} className="mt-1.5" data-testid="reset-password-input" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={submit} disabled={loading} className="bg-[color:var(--primary)] hover:bg-[color:var(--primary-hover)] text-white" data-testid="reset-password-save">{loading ? "Saving…" : "Set password"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
